import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, KeyRound, Pencil } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, type IssuedKey } from '../lib/api';
import {
  formatAgo,
  formatDateTime,
  formatEur,
  formatInt,
  formatUptime,
  providerLabel,
  TIER_LABELS,
} from '../lib/format';
import { Button, Card, ErrorText, Modal, OnlineBadge, Stat } from '../components/ui';
import { SuspendToggle } from '../components/SuspendToggle';
import { InvoicesTab } from '../components/InvoicesTab';
import { ModulesTab } from '../components/ModulesTab';
import { AiTab } from '../components/AiTab';
import { UsageTab } from '../components/UsageTab';
import { AiBudgetBadge } from '../components/AiBudgetBadge';
import { ALL_MODULE_KEYS } from '../lib/moduleCatalog';
import { AuditList } from '../components/AuditList';
import { TenantForm } from '../components/TenantForm';
import { KeyReveal } from '../components/KeyReveal';
import { MobileAppCard } from '../components/MobileAppCard';
import { DesktopAppCard } from '../components/DesktopAppCard';

type Tab = 'overview' | 'modules' | 'ai' | 'usage' | 'invoices' | 'history';

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [editing, setEditing] = useState(false);
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [rotateError, setRotateError] = useState<unknown>(null);

  const tenant = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => api.tenant(id),
    refetchInterval: 15_000,
  });
  const history = useQuery({
    queryKey: ['tenant-audit', id],
    queryFn: () => api.tenantAudit(id),
    enabled: tab === 'history',
  });

  if (tenant.error) return <ErrorText error={tenant.error} />;
  const t = tenant.data;
  if (!t) return <div className="text-slate-400">Se încarcă…</div>;
  const hb = t.latest;

  async function rotate() {
    if (
      !window.confirm(
        'Generezi o cheie nouă? Instanța nu mai e recunoscută până primește cheia nouă în .env.',
      )
    )
      return;
    setRotateError(null);
    try {
      setIssued(await api.rotateKey(id));
    } catch (err) {
      setRotateError(err);
    }
  }

  const tabClass = (value: Tab) =>
    `border-b-2 px-3 py-2 text-sm ${tab === value ? 'border-violet-500 text-white' : 'border-transparent text-slate-400 hover:text-white'}`;

  return (
    <div className="space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" /> Clienți
      </Link>

      <div className="flex flex-wrap items-start gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{t.name}</h1>
            <OnlineBadge online={t.online} />
            <AiBudgetBadge usage={t.aiBudgetUsage} />
          </div>
          <div className="mt-1 text-sm text-slate-400">
            <a
              href={`https://${t.domain}`}
              target="_blank"
              rel="noreferrer"
              className="hover:text-white"
            >
              {t.domain}
            </a>{' '}
            · {t.slug}
            {t.hostLabel && <> · server {t.hostLabel}</>}
            {t.plan && <> · plan {t.plan}</>}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Ultimul semn de viață: {formatAgo(t.lastSeenAt)}
            {t.lastSeenAt && ` (${formatDateTime(t.lastSeenAt)})`} · DOWN după{' '}
            {t.offlineAfterMinutes} min fără heartbeat
          </div>
          {t.status === 'SUSPENDED' && t.suspendReason && (
            <div className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              Mesaj afișat: {t.suspendReason}
            </div>
          )}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4" /> Modifică
          </Button>
          <Button variant="ghost" onClick={() => void rotate()}>
            <KeyRound className="h-4 w-4" /> Cheie nouă
          </Button>
          <SuspendToggle tenantId={t.id} tenantName={t.name} status={t.status} />
        </div>
      </div>
      <ErrorText error={rotateError} />

      <div className="flex gap-1 border-b border-slate-800">
        <button type="button" className={tabClass('overview')} onClick={() => setTab('overview')}>
          Prezentare
        </button>
        <button type="button" className={tabClass('modules')} onClick={() => setTab('modules')}>
          Module
          {t.allowedModules && (
            <span className="ml-1.5 text-xs text-slate-500">
              {t.allowedModules.length}/{ALL_MODULE_KEYS.length}
            </span>
          )}
        </button>
        <button type="button" className={tabClass('ai')} onClick={() => setTab('ai')}>
          AI
          {t.aiModels && (
            <span className="ml-1.5 text-xs text-slate-500">
              {t.aiModels.allowed.map((x) => TIER_LABELS[x]).join('/')}
              {' · '}
              {t.aiModels.default === 'auto' ? 'Auto' : TIER_LABELS[t.aiModels.default]}
            </span>
          )}
        </button>
        <button type="button" className={tabClass('usage')} onClick={() => setTab('usage')}>
          Consum
        </button>
        <button type="button" className={tabClass('invoices')} onClick={() => setTab('invoices')}>
          Facturi
        </button>
        <button type="button" className={tabClass('history')} onClick={() => setTab('history')}>
          Istoric
        </button>
      </div>

      {tab === 'overview' && (
        <div className="space-y-4">
          {hb ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
                <Stat label="Useri activi" value={formatInt(hb.usersActive)} />
                <Stat label="Clienți" value={formatInt(hb.clientsActive)} />
                <Stat label="Furnizori" value={formatInt(hb.suppliersActive)} />
                <Stat label="Abonamente" value={formatInt(hb.subscriptionsActive)} />
                <Stat
                  label="Task-uri"
                  value={formatInt(hb.tasksTotal)}
                  hint={`${formatInt(hb.tasksOpen)} deschise`}
                />
                <Stat label="Proiecte" value={formatInt(hb.projects)} />
                <Stat label="Firme" value={formatInt(hb.companies)} />
                <Stat label="Bază de date" value={`${formatInt(hb.dbSizeMb)} MB`} />
                <Stat
                  label="Versiune"
                  value={hb.version}
                  hint={`pornită de ${formatUptime(hb.uptimeSec)}`}
                />
                <Stat
                  label={`AI ${hb.aiMonth}`}
                  value={formatEur(hb.aiTotalEur)}
                  hint={
                    hb.aiBudgetEur != null
                      ? `din bugetul de ${formatEur(hb.aiBudgetEur)}`
                      : 'fără buget setat'
                  }
                />
              </div>

              <Card>
                <h2 className="text-sm font-semibold">Consum AI pe provider, luna {hb.aiMonth}</h2>
                <table className="mt-3 w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="py-2">Provider</th>
                      <th className="py-2 text-right">Apeluri</th>
                      <th className="py-2 text-right">Cost</th>
                      <th className="py-2 text-right">Din buget</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {Object.entries(hb.aiUsage).map(([provider, u]) => (
                      <tr key={provider}>
                        <td className="py-2">{providerLabel(provider)}</td>
                        <td className="py-2 text-right">{formatInt(u.callCount)}</td>
                        <td className="py-2 text-right">{formatEur(u.costEur)}</td>
                        <td className="py-2 text-right text-slate-400">
                          {hb.aiBudgetEur
                            ? `${Math.round((u.costEur / hb.aiBudgetEur) * 100)}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                    {Object.keys(hb.aiUsage).length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-3 text-slate-400">
                          Niciun consum AI luna aceasta.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Card>

              {t.daily.length > 1 && (
                <Card>
                  <h2 className="text-sm font-semibold">Ultimele 30 de zile</h2>
                  <div className="mt-3 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={t.daily}>
                        <CartesianGrid stroke="#1e293b" />
                        <XAxis
                          dataKey="day"
                          stroke="#64748b"
                          fontSize={11}
                          tickFormatter={(d: string) => d.slice(5)}
                        />
                        <YAxis
                          yAxisId="left"
                          stroke="#64748b"
                          fontSize={11}
                          allowDecimals={false}
                        />
                        <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={11} />
                        <Tooltip
                          contentStyle={{ background: '#0f172a', border: '1px solid #334155' }}
                        />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="usersActive"
                          name="Useri"
                          stroke="#a78bfa"
                          dot={false}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="aiTotalEur"
                          name="AI (EUR)"
                          stroke="#34d399"
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <p className="text-sm text-slate-300">Instanța nu a trimis încă niciun heartbeat.</p>
              <p className="mt-2 text-sm text-slate-400">
                Pune în <code>.env</code>-ul ei <code>CONSOLE_URL</code>, <code>INSTANCE_ID</code>{' '}
                și <code>INSTANCE_KEY</code>, apoi <code>docker compose up -d</code>. Primul
                heartbeat sosește la ~10 secunde după pornire.
              </p>
            </Card>
          )}
          <MobileAppCard slug={t.slug} domain={t.domain} />
          <DesktopAppCard slug={t.slug} domain={t.domain} />
        </div>
      )}

      {tab === 'modules' && (
        // Keyed on the saved list so the draft resets after a save.
        <ModulesTab key={(t.allowedModules ?? ['*']).join()} tenant={t} lastSeenAt={t.lastSeenAt} />
      )}

      {tab === 'ai' && (
        // Keyed on the saved policy so the draft resets after a save.
        <AiTab
          key={JSON.stringify([t.aiModels, t.aiBilling])}
          tenant={t}
          lastSeenAt={t.lastSeenAt}
          aiTotalEur={t.latest?.aiTotalEur ?? null}
        />
      )}

      {tab === 'usage' && <UsageTab tenantId={t.id} />}

      {tab === 'invoices' && <InvoicesTab tenantId={t.id} />}

      {tab === 'history' && (
        <>
          <ErrorText error={history.error} />
          <AuditList entries={history.data ?? []} />
        </>
      )}

      {editing && (
        <Modal title={`Modifică ${t.name}`} onClose={() => setEditing(false)}>
          <TenantForm
            editing
            initial={t}
            submitLabel="Salvează"
            onSubmit={async (input) => {
              await api.updateTenant(t.id, {
                name: input.name,
                domain: input.domain,
                plan: input.plan,
                notes: input.notes,
                hostLabel: input.hostLabel,
              });
              await queryClient.invalidateQueries();
              setEditing(false);
            }}
          />
        </Modal>
      )}

      {issued && (
        <Modal title="Cheie nouă de instanță" onClose={() => setIssued(null)}>
          <KeyReveal issued={issued} consoleUrl={window.location.origin} />
        </Modal>
      )}
    </div>
  );
}
